// src/components/Landing/TopicsSection.js
import React, { useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import './TopicsSection.css';

const topics = [
  {
    to: '/ai-fluency',
    title: 'AI Fluency',
    description: 'Build foundational understanding of artificial intelligence concepts, terminology, and real-world applications.',
    linkText: 'Learn more',
    icon: (
      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
        <circle cx="12" cy="12" r="10" />
        <path d="M12 6v6l4 2" />
      </svg>
    ),
  },
  {
    to: '/agentic-ai',
    title: 'Agentic AI',
    description: 'Explore autonomous AI systems that can reason, plan, and take actions independently in complex environments.',
    linkText: 'Learn more',
    icon: (
      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
        <rect x="2" y="6" width="20" height="12" rx="2" />
        <path d="M12 12h.01M17 12h.01M7 12h.01" />
      </svg>
    ),
  },
  {
    to: '/ethics',
    title: 'AI Ethics',
    description: 'Navigate the ethical dimensions of AI deployment including bias, privacy, transparency, and responsible use.',
    linkText: 'Learn more',
    icon: (
      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
        <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z" />
      </svg>
    ),
  },
  {
    to: '/prompt-engineering',
    title: 'Prompt Engineering',
    description: 'Learn techniques for crafting effective prompts that get the most useful and accurate responses from AI systems.',
    linkText: 'Learn more',
    icon: (
      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
        <polyline points="16 18 22 12 16 6" />
        <polyline points="8 6 2 12 8 18" />
      </svg>
    ),
  },
  {
    to: '/prompt-library',
    title: 'Prompt Library',
    description: 'Access ready-made AI personas and prompt templates designed specifically for supply chain scenarios.',
    linkText: 'Learn more',
    icon: (
      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
        <path d="M4 19.5A2.5 2.5 0 016.5 17H20" />
        <path d="M6.5 2H20v20H6.5A2.5 2.5 0 014 19.5v-15A2.5 2.5 0 016.5 2z" />
      </svg>
    ),
  },
  {
    to: '/podcasts',
    title: 'Podcasts',
    description: 'Listen to AI-generated podcast episodes covering supply chain topics, industry trends, and case studies.',
    linkText: 'Listen now',
    icon: (
      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
        <path d="M12 1a3 3 0 00-3 3v8a3 3 0 006 0V4a3 3 0 00-3-3z" />
        <path d="M19 10v2a7 7 0 01-14 0v-2" />
        <line x1="12" y1="19" x2="12" y2="23" />
        <line x1="8" y1="23" x2="16" y2="23" />
      </svg>
    ),
  },
];

export function TopicsSection() {
  const sectionRef = useRef(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.classList.add('visible');
          }
        });
      },
      { threshold: 0.15 }
    );

    const reveals = sectionRef.current?.querySelectorAll('.reveal');
    reveals?.forEach((el) => observer.observe(el));

    return () => observer.disconnect();
  }, []);

  return (
    <section className="topics" ref={sectionRef}>
      <div className="container">
        <div className="section-header reveal">
          <span className="section-label">Explore Topics</span>
          <div className="section-rule"></div>
        </div>
        <div className="topics-grid">
          {topics.map((topic, index) => (
            <Link
              key={topic.to}
              to={topic.to}
              className={`topic-item reveal reveal-delay-${index + 1}`}
            >
              <div className="topic-icon">{topic.icon}</div>
              <h3 className="topic-title">{topic.title}</h3>
              <p className="topic-desc">{topic.description}</p>
              <span className="topic-link">
                {topic.linkText}
                <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <path d="M5 12h14M12 5l7 7-7 7" />
                </svg>
              </span>
            </Link>
          ))}
        </div>
      </div>
    </section>
  );
}
